/** @jsx jsx */
import React, { useState, useEffect } from "react";
import { jsx, css } from "@emotion/core";
import { Formik, Field } from "formik";
import { Redirect } from "react-router";

import { get, post } from "../api";

import PrimaryCTAButton from "../../components/PrimaryCTA";
import Form from "../../components/Form";
import ValidationError from "../../components/ValidationError";
import RadioButton from "../../components/RadioButton";
import RadioGroup from "../../components/RadioGroup";
import ToolTipButton from "../../components/ToolTipButton";

import { resourceUrls } from "../../config";
import { handleGet, handlePost } from "../api/handler";
import PageContainer from "../components/PageContainer";

const validator = values => {
    let errors = {};

    if (!values.workEligibility) {
        errors.workEligibility = "Please select a work eligibility status.";
    }

    return errors;
};
const initialValues = {
    workEligibility: null,
};

const AdditionalInformation = props => (
    <p {...props}>
        We'd love to see your application, but if you're on a Tier
        4 visa or from outside of the UK, you may be legally prevented from
        working as a self employed individual. Tier 4 visas allow 20 hours of
        work, but as a MyTutor tutor you would be self-employed
    </p>
);

const getEligibilities = (
    setIsLoading,
    setIsLoggedIn,
    setError,
    setEligibilities
) => {
    handleGet({
        getApiCall: () =>
            get({ url: resourceUrls.workEligibility.getEligibilities }),
        setIsLoading,
        setIsLoggedIn,
        setError,
        setData: setEligibilities,
    });
};

const submitWorkEligibility = (
    setIsLoggedIn,
    setError,
    setFormSubmissionComplete
) => async (values, actions) => {
    if (!values.workEligibility) {
        return { error: true };
    }

    const body = {
        workEligibility: values.workEligibility,
        applicationStep: "ELIGIBILITY",
    };

    await handlePost({
        postApiCall: () =>
            post({ url: resourceUrls.workEligibility.submit, body: body }),
        setIsLoggedIn: setIsLoggedIn,
        setError: setError,
        setIsSubmissionComplete: setFormSubmissionComplete,
    });

    actions.setSubmitting(false);
};

const WorkEligibility = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(true);
    const [error, setError] = useState(null);
    const [eligibilities, setEligibilities] = useState([]);
    const [showAdditionalInformation, setShowAdditionalInformation] = useState(
        false
    );
    const [formSubmissionComplete, setFormSubmissionComplete] = useState(false);

    useEffect(() => {
        getEligibilities(
            setIsLoading,
            setIsLoggedIn,
            setError,
            setEligibilities
        );
    }, []);

    if (formSubmissionComplete) {
        return (
            <Redirect
                push
                to={{
                    pathname: "/secure/apply",
                }}
            />
        );
    }

    return (
        <>
            <PageContainer
                isLoading={isLoading}
                error={error}
                isLoggedIn={isLoggedIn}
            >
                <h2
                    css={css`
                        max-width: 90%;
                    `}
                >
                    Are you eligible to work in the UK?{" "}
                    <ToolTipButton
                        onClick={() =>
                            setShowAdditionalInformation(
                                !showAdditionalInformation
                            )
                        }
                        data-testid="eligibility-tooltip-button"
                    />
                </h2>
                {showAdditionalInformation && (
                    <AdditionalInformation data-testid="eligibility-additional-information" />
                )}
                <p>Please select an option:</p>

                <Formik
                    initialValues={initialValues}
                    validate={validator}
                    validateOnBlur={false}
                    validateOnChange={true}
                    onSubmit={submitWorkEligibility(
                        setIsLoggedIn,
                        setError,
                        setFormSubmissionComplete
                    )}
                    render={({isSubmitting, ...props }) => (
                        <Form onSubmit={props.handleSubmit}>
                            {Object.keys(eligibilities).map(eligibility => (
                                <RadioGroup>
                                    <Field
                                        component={RadioButton}
                                        name="workEligibility"
                                        id={eligibility}
                                        label={eligibilities[eligibility]}
                                    />
                                </RadioGroup>
                            ))}

                            {props.errors.workEligibility && (
                                <ValidationError>
                                    {props.errors.workEligibility}
                                </ValidationError>
                            )}
                            <PrimaryCTAButton type="submit" data-testid="next-button" disabled={isSubmitting}>Next →</PrimaryCTAButton>
                        </Form>
                    )}
                />
            </PageContainer>
        </>
    );
};

export default WorkEligibility;
